<template>
  <div class="credits-page">
    <div class="page-container">
      <header class="page-header">
        <h1 class="page-title">Buy Edit Credits</h1>
        <p class="page-subtitle">Purchase credits to use for manuscript edits at any time</p>
      </header>

      <!-- Current Balance -->
      <div v-if="authStore.isAuthenticated" class="current-balance">
        <div class="balance-card">
          <div class="balance-label">Current Balance</div>
          <div class="balance-value">{{ creditBalance }} {{ creditBalance === 1 ? 'edit' : 'edits' }}</div>
        </div>
      </div>

      <!-- Promo Banner -->
      <div v-if="promoCode && promoDiscount !== null" class="promo-banner">
        <strong>{{ promoCode }}</strong> applied — {{ Math.round(promoDiscount * 100) }}% off at checkout
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading packages...</p>
      </div>

      <!-- Credit Packages -->
      <div v-else class="packages-grid">
        <div
          v-for="pkg in packages"
          :key="pkg.tier"
          class="package-card"
          :class="{ recommended: pkg.tier === 'tier_6' }"
        >
          <div v-if="pkg.tier === 'tier_6'" class="recommended-badge">Most Popular</div>

          <div class="package-header">
            <h3 class="package-name">{{ pkg.name }}</h3>
            <p class="package-description">{{ pkg.description }}</p>
          </div>

          <div class="package-pricing">
            <div v-if="discountedPrice(pkg.priceInCents) !== null" class="package-price-original">
              ${{ (pkg.priceInCents / 100).toFixed(0) }}
            </div>
            <div class="package-price">
              ${{ discountedPrice(pkg.priceInCents) ?? (pkg.priceInCents / 100).toFixed(0) }}
            </div>
            <div class="package-credits">{{ pkg.credits }} {{ pkg.credits === 1 ? 'edit' : 'edits' }}</div>
            <div class="package-per-edit">${{ pkg.pricePerEdit }} per edit</div>
          </div>

          <div class="package-savings">
            <svg class="savings-icon" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            <span>Save {{ pkg.savings }}% vs per-word pricing</span>
          </div>

          <button @click="purchasePackage(pkg.tier)" class="purchase-button" :disabled="!!purchasing">
            {{ purchasing === pkg.tier ? 'Processing...' : 'Buy Credits' }}
          </button>
        </div>
      </div>

      <!-- FAQ Section -->
      <div class="faq-section">
        <h2 class="faq-title">How Credits Work</h2>

        <div class="faq-item">
          <h3>What is a credit?</h3>
          <p>
            One credit equals one manuscript edit, regardless of word count or which editing frameworks you select. Use
            your credits any time for any manuscript.
          </p>
        </div>

        <div class="faq-item">
          <h3>Do credits expire?</h3>
          <p>No, your credits never expire. Purchase now and use them whenever you're ready.</p>
        </div>

        <div class="faq-item">
          <h3>Can I use multiple frameworks with one credit?</h3>
          <p>
            Yes! One credit covers one manuscript edit with any combination of frameworks you choose (Novel, Short
            Story, Memoir, Non-Fiction, or Roast).
          </p>
        </div>

        <div class="faq-item">
          <h3>What if I want to pay per word instead?</h3>
          <p>
            No problem! At checkout, you can choose to either use a credit or pay $0.003 per word. The choice is yours.
          </p>
        </div>
      </div>

      <p v-if="error" class="error-message">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { getGaIds } from '../lib/analytics';
import { getAttribution, getTouches } from '../lib/attribution';
import { getRewardfulReferral } from '../lib/rewardful';
import { useRoute, useRouter } from 'vue-router';
import { fetchWithAuth } from '../services/api';
import { useAuthStore } from '../stores/auth';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const loading = ref(true);
const error = ref('');
const creditBalance = ref(0);
const packages = ref<any[]>([]);
const purchasing = ref<string | null>(null);

const promoCode = computed(() => (route.query['promo'] as string) || null);
const PROMO_DISCOUNTS: Record<string, number> = { ROAST20: 0.2 };
const promoDiscount = computed(() => (promoCode.value ? (PROMO_DISCOUNTS[promoCode.value] ?? null) : null));

function discountedPrice(priceInCents: number): number | null {
  if (promoDiscount.value === null) return null;
  return Math.round((priceInCents * (1 - promoDiscount.value)) / 100);
}

async function loadPackages() {
  loading.value = true;
  error.value = '';

  try {
    const response = await fetch(`${import.meta.env['VITE_API_URL'] || ''}/api/credits/packages`);
    if (!response.ok) {
      throw new Error('Failed to load packages');
    }
    const data = await response.json();
    packages.value = data.packages || [];
  } catch (err) {
    error.value = 'Failed to load credit packages. Please try again.';
    console.error('Failed to load packages:', err);
  } finally {
    loading.value = false;
  }
}

async function loadCreditBalance() {
  if (!authStore.uid) return;

  try {
    const response = await fetchWithAuth(`/api/users/${authStore.uid}/credits`);
    if (!response.ok) return;
    const data = await response.json();
    creditBalance.value = data.balance || 0;
  } catch (err) {
    console.error('Failed to load credit balance:', err);
  }
}

async function purchasePackage(tier: string) {
  purchasing.value = tier;
  error.value = '';

  try {
    const referral = await getRewardfulReferral();
    const { clientId: gaClientId, sessionId: gaSessionId } = getGaIds();

    const response = await fetchWithAuth('/api/credits/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        packageTier: tier,
        promoCode: promoCode.value || undefined,
        referral,
        gaClientId,
        gaSessionId,
        attribution: getAttribution(),
        touches: getTouches(),
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to create checkout');
    }

    const data = await response.json();
    window.location.href = data.redirectUrl;
  } catch (err: any) {
    error.value = err.message || 'Failed to start checkout. Please try again.';
    console.error('Purchase error:', err);
    purchasing.value = null;
  }
}

onMounted(() => {
  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth', query: { redirect: '/credits' } });
  } else {
    loadPackages();
    loadCreditBalance();
  }
});
</script>

<style scoped>
.credits-page {
  flex: 1;
  background: #f7fafc;
}

.page-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
}

.page-header {
  text-align: center;
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.125rem;
  color: #718096;
}

/* Current Balance */
.current-balance {
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
}

.balance-card {
  background: white;
  border: 2px solid var(--color-accent);
  border-radius: 12px;
  padding: 1.5rem 3rem;
  text-align: center;
}

.balance-label {
  font-size: 0.875rem;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.balance-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-accent);
}

/* Promo Banner */
.promo-banner {
  text-align: center;
  padding: 0.75rem 1.5rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  color: #166534;
  font-size: 0.925rem;
  margin-bottom: 2rem;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 4rem 2rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  color: #718096;
  font-size: 1rem;
}

/* Packages Grid */
.packages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
}

.package-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.2s;
}

.package-card:hover {
  border-color: var(--color-accent);
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.package-card.recommended {
  border-color: var(--color-accent);
  border-width: 3px;
}

.recommended-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-accent);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 1rem;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.package-header {
  margin-bottom: 1.5rem;
}

.package-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.package-description {
  font-size: 0.875rem;
  color: #718096;
  margin: 0;
}

.package-pricing {
  text-align: center;
  margin-bottom: 1.5rem;
}

.package-price-original {
  font-size: 1.25rem;
  color: #a0aec0;
  text-decoration: line-through;
  text-decoration-thickness: 2px;
  margin-bottom: 0.125rem;
}

.package-price {
  font-size: 3rem;
  font-weight: 700;
  color: #1a202c;
  line-height: 1;
  margin-bottom: 0.5rem;
}

.package-credits {
  font-size: 1.125rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.25rem;
}

.package-per-edit {
  font-size: 0.875rem;
  color: #a0aec0;
}

.package-savings {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #f0fdf4;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  color: #166534;
  font-size: 0.875rem;
  font-weight: 600;
}

.savings-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.purchase-button {
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  background: var(--color-accent);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  margin-top: auto;
}

.purchase-button:hover:not(:disabled) {
  background: var(--color-accent-hover);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(97, 174, 212, 0.3);
}

.purchase-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* FAQ Section */
.faq-section {
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 0;
}

.faq-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a202c;
  text-align: center;
  margin-bottom: 2rem;
}

.faq-item {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
}

.faq-item h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.faq-item p {
  font-size: 0.875rem;
  color: #4a5568;
  line-height: 1.6;
  margin: 0;
}

.error-message {
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 6px;
  font-weight: 500;
  text-align: center;
}

@media (max-width: 768px) {
  .packages-grid {
    grid-template-columns: 1fr;
  }

  .page-title {
    font-size: 2rem;
  }
}
</style>
